export type ClinicalDashboardHref =
  | { kind: "hub"; segment: string }
  | { kind: "absolute"; href: string }

export type ClinicalDashboardBadge = "new" | "beta" | "leo" | "approve"

export interface ClinicalDashboardItem {
  id: string
  label: string
  description: string
  icon: string
  href: ClinicalDashboardHref
  badges?: ClinicalDashboardBadge[]
  /** Shows the external-door mark next to the title. */
  external?: boolean
}

export interface ClinicalDashboardColumn {
  id: string
  title: string
  items: ClinicalDashboardItem[]
  /** Items after this index sit under a hairline (Program: Reports). */
  divideAfterIndex?: number
  showCalendar?: boolean
  /** Token class for the column’s icons (screenshot hues via chart/brand). */
  iconClass: string
}

export const CLINICAL_DASHBOARD_COLUMNS: ClinicalDashboardColumn[] = [
  {
    id: "program",
    title: "Program",
    iconClass: "text-brand",
    showCalendar: true,
    divideAfterIndex: 2,
    items: [
      {
        id: "program-details",
        label: "Program Details",
        description:
          "Set up defining attributes of the program including mission, vision, accreditation standards and more.",
        icon: "fa-light fa-landmark",
        href: { kind: "hub", segment: "program-details" },
        external: true,
      },
      {
        id: "students",
        label: "Students",
        description:
          "Manage profiles and demographic information on matriculated students and alumni.",
        icon: "fa-light fa-graduation-cap",
        href: { kind: "hub", segment: "students" },
        external: true,
      },
      {
        id: "faculty-staff",
        label: "Faculty and Staff",
        description:
          "View faculty portfolios including teaching responsibility, scholarly productivity and service activities.",
        icon: "fa-light fa-id-card",
        href: { kind: "hub", segment: "faculty-staff" },
        external: true,
      },
      {
        id: "reports",
        label: "Reports",
        description:
          "Access all standard, custom, and accreditation reports across the system from a centralized location.",
        icon: "fa-light fa-chart-line",
        href: { kind: "hub", segment: "reports" },
      },
    ],
  },
  {
    id: "curriculum",
    title: "Curriculum Management",
    iconClass: "text-chart-5",
    items: [
      {
        id: "courses",
        label: "Courses",
        description:
          "Enter, organize and manage courses including course documents, schedules, resources and registration.",
        icon: "fa-light fa-book-open",
        href: { kind: "hub", segment: "courses" },
        external: true,
      },
      {
        id: "competency-management",
        label: "Competency Management",
        description:
          "Set up competency frameworks, map course offerings to competencies, and review student scores.",
        icon: "fa-light fa-person-rays",
        href: { kind: "hub", segment: "competency-management" },
        external: true,
      },
    ],
  },
  {
    id: "placement",
    title: "Placement Management",
    iconClass: "text-chart-2",
    items: [
      {
        id: "sites",
        label: "Sites",
        description:
          "Find contact information, contracts, availability and clearance requirements for all clinical sites.",
        icon: "fa-light fa-building",
        href: { kind: "hub", segment: "sites" },
      },
      {
        id: "process-my-requests",
        label: "Process My Requests",
        description: "Process placement requests submitted by students.",
        icon: "fa-light fa-inbox-in",
        href: { kind: "hub", segment: "process-my-requests" },
      },
      {
        id: "placements",
        label: "Placements",
        description: "Navigate the process of matching students to sites from start to finish.",
        icon: "fa-light fa-stethoscope",
        href: { kind: "hub", segment: "placements" },
      },
      {
        id: "learning-activities",
        label: "Learning Activities",
        description: "Set up, distribute, review and grade course related activities.",
        icon: "fa-light fa-clipboard-list",
        href: { kind: "hub", segment: "learning-activities" },
      },
    ],
  },
]

/** Opens another Exxat product (utility bar switches product root). */
export const CLINICAL_DASHBOARD_EXTERNAL_HREFS: Partial<Record<string, string>> = {
  "program-details": "/programs",
  students: "/people/students",
  "faculty-staff": "/people/faculty",
  courses: "/courses",
  "competency-management": "/student-success/competency-management",
  "curriculum-mapping": "/curriculum-mapping/dashboard",
  "student-compliance": "/compliance/student",
  "faculty-compliance": "/compliance/faculty",
}

/** Hub segments that open another Exxat product (sidebar + launcher external mark). */
export function clinicalDashboardExternalSegments(): string[] {
  const segments: string[] = []
  for (const column of CLINICAL_DASHBOARD_COLUMNS) {
    for (const item of column.items) {
      if (!item.external) continue
      if (item.href.kind === "hub") segments.push(item.href.segment)
    }
  }
  return segments
}

export function clinicalDashboardHref(
  item: ClinicalDashboardItem,
  productRoot: string,
): string {
  if (item.external) {
    const external = CLINICAL_DASHBOARD_EXTERNAL_HREFS[item.id]
    if (external) return external
  }
  if (item.href.kind === "absolute") return item.href.href
  return `/${productRoot}/${item.href.segment}`
}

export function findClinicalDashboardItem(id: string | null): ClinicalDashboardItem | undefined {
  if (!id) return undefined
  for (const column of CLINICAL_DASHBOARD_COLUMNS) {
    const match = column.items.find(item => item.id === id)
    if (match) return match
  }
  return undefined
}
